/**
 * The Italian Masterclass — premium motion (MasterClass-inspired)
 * Sticky glass header, scroll reveals, hero parallax, chapter rail.
 */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function initAccountMenu() {
    const menus = document.querySelectorAll('.im-account-menu');
    if (!menus.length) return;

    document.addEventListener('click', (event) => {
      menus.forEach((menu) => {
        if (!menu.open || menu.contains(event.target)) return;
        menu.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      menus.forEach((menu) => menu.removeAttribute('open'));
    });
  }

  function initStickyHeader() {
    const header = document.querySelector('.header-wrapper, sticky-header');
    if (!header) return;

    const sync = () => {
      const scrolled = window.scrollY > 24;
      document.documentElement.classList.toggle('im-scrolled', scrolled);
      header.classList.toggle('im-header--scrolled', scrolled);
    };

    sync();
    window.addEventListener('scroll', sync, { passive: true });
  }

  function initReveals() {
    const nodes = document.querySelectorAll(
      '.im-reveal, .mc-skill, .mc-learn__card, .mc-testimonial, .mc-value__statement, .mc-skills__header, .mc-cta .im-display, .mc-cta .im-lede, .mc-cta .im-cta-row, .mc-cta__logo, .mc-mary__media, .mc-mary__copy, .mc-philosophy__copy, .mc-secrets__copy, .mc-beginning__inner, .mc-mid-trailer__frame, .mc-mary-quote'
    );
    if (!nodes.length) return;

    nodes.forEach((el, i) => {
      el.classList.add('im-reveal');
      if (!el.style.getPropertyValue('--im-reveal-delay')) {
        el.style.setProperty('--im-reveal-delay', `${Math.min(i % 8, 7) * 70}ms`);
      }
    });

    document.querySelectorAll('.mc-mary__media, .mc-learn__card').forEach((el) => {
      el.classList.add('im-reveal--scale');
    });
    document.querySelectorAll('.mc-mary__copy, .mc-value__statement').forEach((el) => {
      el.classList.add('im-reveal--left');
    });
    document.querySelectorAll('.mc-mid-trailer__frame, .mc-mary-quote').forEach((el) => {
      el.classList.add('im-reveal--slow');
    });
    document.querySelectorAll('.mc-philosophy__copy, .mc-secrets__copy').forEach((el) => {
      el.classList.add('im-reveal--fast');
      el.style.setProperty('--im-reveal-delay', '0ms');
    });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((el) => el.classList.add('im-reveal--in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('im-reveal--in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -4% 0px', threshold: 0.06 }
    );

    nodes.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
        el.classList.add('im-reveal--in');
      } else {
        io.observe(el);
      }
    });
  }

  function initSectionParallax() {
    if (reduceMotion) return;

    const layers = [
      { root: '.mc-mary', media: '.mc-mary__media', strength: 1 },
      { root: '.mc-philosophy', media: '.mc-philosophy__media', strength: 1.25 },
      { root: '.mc-secrets', media: '.mc-secrets__media', strength: 1.2 },
    ];

    const items = layers
      .map(({ root, media, strength }) => {
        const section = document.querySelector(root);
        const layer = section?.querySelector(media);
        return section && layer ? { section, layer, strength } : null;
      })
      .filter(Boolean);

    if (!items.length) return;

    let ticking = false;
    const update = () => {
      items.forEach(({ section, layer, strength }) => {
        const rect = section.getBoundingClientRect();
        const view = window.innerHeight || 1;
        const progress = (view - rect.top) / (view + rect.height);
        const clamped = Math.min(Math.max(progress, 0), 1);
        layer.style.setProperty('--im-parallax', String((clamped - 0.5) * 2 * strength));
        if (section.classList.contains('mc-philosophy') || section.classList.contains('mc-secrets')) {
          if (rect.top < view * 0.75 && rect.bottom > view * 0.2) {
            section.classList.add('is-cinematic-in');
          }
        }
      });
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function initHeroParallax() {
    const hero = document.querySelector('.mc-hero');
    if (!hero || reduceMotion) return;

    const layer = hero.querySelector('.mc-hero__media') || hero.querySelector('.mc-hero__collage');
    if (!layer) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = hero.getBoundingClientRect();
        const progress = Math.min(Math.max(-rect.top / Math.max(rect.height, 1), 0), 1);
        layer.style.setProperty('--im-parallax', String(progress));
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initHeroVideo() {
    const hero = document.querySelector('[data-im-hero-video]');
    if (!hero) return;

    const video =
      hero.querySelector('video.mc-hero__video') ||
      hero.querySelector('[data-im-hero-video-el]');
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;

    const tryPlay = () => {
      if (reduceMotion) {
        video.pause();
        return;
      }
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const armSource = () => {
      const src = video.getAttribute('data-im-hero-src');
      if (src && !video.getAttribute('src') && !video.querySelector('source')) {
        video.setAttribute('src', src);
        video.load();
      }
      video.addEventListener('loadeddata', () => {
        hero.classList.add('is-video-ready');
        tryPlay();
      }, { once: true });
      tryPlay();
    };

    const scheduleLoad = () => {
      if (reduceMotion) return;
      const run = () => armSource();
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1800 });
      } else {
        setTimeout(run, 700);
      }
    };

    scheduleLoad();

    if (!('IntersectionObserver' in window)) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (reduceMotion) return;
          if (entry.isIntersecting) {
            armSource();
            tryPlay();
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(hero);
  }

  function initChapterRail() {
    document.querySelectorAll('[data-im-rail]').forEach((rail) => {
      const track = rail.querySelector('[data-im-rail-track]');
      const prev = rail.querySelector('[data-im-rail-prev]');
      const next = rail.querySelector('[data-im-rail-next]');
      if (!track) return;

      const step = () => Math.min(track.clientWidth * 0.78, 420);

      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      prev?.addEventListener('click', () => {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      next?.addEventListener('click', () => {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  function mountVimeoEmbed(host, title) {
    if (!host || host.querySelector('iframe')) return null;
    const src = host.getAttribute('data-im-embed-src');
    if (!src) return null;
    const iframe = document.createElement('iframe');
    iframe.className = host.classList.contains('mc-trailer__embed-host') ? 'mc-trailer__embed' : 'mc-mid-trailer__embed';
    iframe.src = src;
    iframe.setAttribute('frameborder', '0');
    iframe.allow = 'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.setAttribute('allowfullscreen', '');
    iframe.title = title || 'The Italian Masterclass';
    host.appendChild(iframe);
    return iframe;
  }

  function initMidTrailer() {
    document.querySelectorAll('[data-im-mid-trailer]').forEach((root) => {
      const playBtn = root.querySelector('[data-im-mid-trailer-play]');
      const video = root.querySelector('[data-im-mid-trailer-video]') || root.querySelector('video');
      const embedHost = root.querySelector('[data-im-mid-trailer-embed]');
      if (!video && !embedHost) return;

      const src = video?.getAttribute('data-im-trailer-src');
      const play = () => {
        if (embedHost) {
          mountVimeoEmbed(embedHost, playBtn?.getAttribute('aria-label'));
          root.classList.add('is-playing');
          return;
        }
        if (src && video && !video.getAttribute('src')) {
          video.setAttribute('src', src);
          video.load();
        }
        if (video) {
          video.muted = true;
          video.defaultMuted = true;
          video.setAttribute('muted', '');
        }
        root.classList.add('is-playing');
        video?.play?.().catch(() => {});
      };
      playBtn?.addEventListener('click', play);

      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && video && !video.paused) video.pause();
          });
        },
        { threshold: 0.2 }
      );
      io.observe(root);
    });
  }

  function initTrailer() {
    const root = document.querySelector('[data-im-product-hero]');
    if (!root) return;
    const modal = root.querySelector('[data-im-trailer]');
    if (!modal) return;

    const openers = root.querySelectorAll('[data-im-trailer-open]');
    const closers = modal.querySelectorAll('[data-im-trailer-close]');
    const video = modal.querySelector('video');
    const embedHost = modal.querySelector('[data-im-trailer-embed]');
    const src = video?.getAttribute('data-im-trailer-src');

    const open = () => {
      modal.hidden = false;
      document.documentElement.classList.add('im-trailer-open');
      if (embedHost) {
        mountVimeoEmbed(embedHost, modal.getAttribute('aria-label'));
        return;
      }
      if (video) {
        if (src && !video.getAttribute('src')) {
          video.setAttribute('src', src);
          video.load();
        }
        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');
        video.play?.().catch(() => {});
      }
    };

    const close = () => {
      modal.hidden = true;
      document.documentElement.classList.remove('im-trailer-open');
      if (video) {
        video.pause?.();
        video.currentTime = 0;
      }
      if (embedHost) {
        embedHost.replaceChildren();
        return;
      }
      const iframe = modal.querySelector('iframe');
      if (iframe) {
        const s = iframe.src;
        iframe.src = s;
      }
    };

    openers.forEach((btn) => btn.addEventListener('click', open));
    closers.forEach((btn) => btn.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  function initStickyBuy() {
    const hero = document.querySelector('[data-im-product-hero]');
    const bar = document.querySelector('[data-im-sticky-buy]');
    if (!hero || !bar) return;

    bar.hidden = false;

    const sync = () => {
      const rect = hero.getBoundingClientRect();
      const show = rect.bottom < 80;
      bar.classList.toggle('is-visible', show);
    };

    sync();
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
  }

  function initSmoothAnchors() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href');
      if (!id || id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      history.pushState(null, '', id);
    });
  }

  /** Keep IM branding vars on top of Courses Plus runtime styles. */
  function initCoursesPlusTheme() {
    const onCourses = /\/apps\/my\/course/.test(window.location.pathname);
    if (!onCourses) return;

    document.body.classList.add('im-courses', 'im-cp-loading');
    const ACCENT = '#e51a4c';
    const TRACK = 'rgba(255, 255, 255, 0.14)';
    const i18n = window.IM_I18N || {};

    const ensureLoader = () => {
      let loader = document.querySelector('.im-cp-loader');
      if (loader) return loader;
      loader = document.createElement('div');
      loader.className = 'im-cp-loader';
      loader.setAttribute('aria-live', 'polite');
      loader.setAttribute('aria-busy', 'true');
      loader.innerHTML =
        '<div class="im-cp-loader__ring" aria-hidden="true"></div><span class="im-cp-loader__label">' +
        (i18n.loading || 'Caricamento') +
        '</span>';
      document.body.prepend(loader);
      return loader;
    };

    const isCoursesReady = () => {
      const hasSkeleton = !!document.querySelector(
        '.skeletonMainHeadBlock, .skeletonLeftSection, .skeletonContentBlock, .skeletonSettingsHead'
      );
      if (hasSkeleton) return false;

      const listVisible = document.querySelector('.courseListWrapper');
      const listShown = listVisible && getComputedStyle(listVisible).display !== 'none';
      if (listShown) {
        return !!document.querySelector('.courseBlock img, .courseBlock .infoWrapper h4');
      }

      const lessonReady = !!document.querySelector(
        '#courseTitle, #appendLesson .videoWrapper, #appendLesson iframe, #treeView .cLessonTitle'
      );
      return lessonReady;
    };

    const setLoading = (loading) => {
      const loader = ensureLoader();
      document.body.classList.toggle('im-cp-loading', loading);
      loader.setAttribute('aria-busy', loading ? 'true' : 'false');
    };

    const setText = (el, value) => {
      if (!el || value == null || value === '') return;
      if (el.textContent === value) return;
      el.textContent = value;
    };

    const looksLikeCourseTitle = (text) =>
      /masterclass|cucina|cocina|cooking|italiana|italian/i.test(text || '');

    const localizeLessonTitle = (text) => {
      const raw = (text || '').trim();
      if (!raw) return '';
      const t = raw.toLowerCase();
      const lessons = i18n.lessons || {};
      if (/tagliatell/.test(t)) return lessons.tagliatelle || 'Tagliatelle';
      if (/tortellin/.test(t)) return lessons.tortellini || 'Tortellini';
      if (/lasagna|lasaña|lasana/.test(t)) return lessons.lasagna || 'Lasagna';
      if (/backstage/.test(t)) return lessons.backstage || 'Backstage';
      if (/trailer|tráiler|tr[aá]iler/.test(t)) return lessons.trailer || raw;
      return '';
    };

    const polishCopy = () => {
      if (i18n.welcome) {
        document.querySelectorAll('.welcomeText p').forEach((lede) => {
          if (
            /all of your courses are listed below|qui trovi i tuoi corsi|aquí están tus cursos/i.test(
              lede.textContent || ''
            )
          ) {
            setText(lede, i18n.welcome);
          }
        });
      }

      if (i18n.backToCourses) {
        document.querySelectorAll('a[href*="/apps/my/courses/list"]').forEach((link) => {
          const label = link.querySelector('span, strong, em') || link;
          if (/torna|back to|volver|miei corsi|my courses|mis cursos/i.test(link.textContent || '')) {
            setText(label, i18n.backToCourses);
          }
        });
      }

      const chrome = document.querySelector('#viewCoursePage, #treeViewWrapper, .courseTitleBlock, .courseListWrapper');
      if (chrome && (i18n.contents || i18n.chaptersHeading)) {
        chrome.querySelectorAll('a, button, h2, h3, h4, p').forEach((el) => {
          const t = (el.textContent || '').trim();
          if (i18n.contents && /^contenuto del corso$|^course contents?$|^contenido del curso$/i.test(t)) {
            setText(el, i18n.contents);
          }
          if (
            i18n.chaptersHeading &&
            /^cap[ií]tulos del curso$|^chapter[s]? of the course$|^capitoli del corso$|^i capitoli$/i.test(t)
          ) {
            setText(el, i18n.chaptersHeading);
          }
        });
      }

      if (i18n.courseTitle) {
        document
          .querySelectorAll('#courseTitle, .courseTitleBlock h2, .infoWrapper h4, .absoluteInfo h4')
          .forEach((el) => {
            if (looksLikeCourseTitle(el.textContent)) setText(el, i18n.courseTitle);
          });
      }

      if (i18n.descriptionHtml) {
        const descTargets = document.querySelectorAll('#listDesc, .courseTitleBlock #listDesc');
        descTargets.forEach((el) => {
          if (el.dataset.imDesc === '1') return;
          el.innerHTML = i18n.descriptionHtml;
          el.dataset.imDesc = '1';
        });
        if (!descTargets.length) {
          document.querySelectorAll('.courseTitleBlock p, .leftProgress p, .courseTitleBlock h4').forEach((el) => {
            const stale =
              /corso di prova|curso de prueba|tr[aá]iler oficial|trailer ufficiale|gestos de la pasta|dalla farina|desde la harina|6 cap[ií]tol/i.test(
                el.textContent || ''
              );
            if (!stale || el.dataset.imDesc === '1') return;
            const host = el.closest('.leftProgress, .courseTitleBlock') || el.parentElement;
            if (!host || host.dataset.imDesc === '1') return;
            host.querySelectorAll('p, h4, ul, ol').forEach((node) => {
              if (node.id === 'courseTitle' || node.matches('h2')) return;
              node.remove();
            });
            const wrap = document.createElement('div');
            wrap.id = 'listDesc';
            wrap.innerHTML = i18n.descriptionHtml;
            wrap.dataset.imDesc = '1';
            host.appendChild(wrap);
            host.dataset.imDesc = '1';
          });
        }
      }

      document.querySelectorAll('.cLessonTitle').forEach((el) => {
        const next = localizeLessonTitle(el.textContent);
        if (next) setText(el, next);
      });

      document.querySelectorAll('.im-cp-lesson-title, #appendLesson h1, #appendLesson h2').forEach((el) => {
        const next = localizeLessonTitle(el.textContent);
        if (next) setText(el, next);
      });

      if (i18n.sectionMain || i18n.sectionExtra) {
        document.querySelectorAll('.cSectionTitle, .sectionTitle, .ccName').forEach((el) => {
          const t = (el.textContent || '').trim();
          if (/introduzione|introduction|introducci[oó]n|cucina italiana|italian cooking/i.test(t)) {
            setText(el, i18n.sectionMain);
          } else if (/contenuti aggiuntivi|additional|contenido adicional|extras?/i.test(t)) {
            setText(el, i18n.sectionExtra);
          }
        });
      }

      if (i18n.lessonsCompleted) {
        document.querySelectorAll('#treeView small, #treeViewWrapper small, .sectionProgress').forEach((el) => {
          const t = (el.textContent || '').trim();
          const match = t.match(/^(\d+)\s*\/\s*(\d+)\s+(lezioni completate|lessons completed|lecciones completadas)$/i);
          if (!match) return;
          setText(
            el,
            i18n.lessonsCompleted.replace('{{ done }}', match[1]).replace('{{ total }}', match[2])
          );
        });
      }
    };

    const ensureLessonTitle = () => {
      const append = document.querySelector('#appendLesson');
      if (!append || getComputedStyle(append).display === 'none') return;

      const active =
        document.querySelector('.list-group-item.cViewved .cLessonTitle') ||
        document.querySelector('#treeView .cViewved .cLessonTitle');
      const titleText = active?.textContent?.trim();
      if (!titleText) return;

      let heading = append.querySelector('.im-cp-lesson-title');
      const hasNativeHeading = append.querySelector('h1, h2');
      if (hasNativeHeading && !heading) return;

      if (!heading) {
        heading = document.createElement('h1');
        heading.className = 'im-cp-lesson-title';
        append.prepend(heading);
      }
      if (heading.textContent !== titleText) heading.textContent = titleText;
    };

    const retintProgress = () => {
      document.querySelectorAll('.rightProgress svg [stroke], .precircleBlock svg [stroke]').forEach((node) => {
        const s = node.getAttribute('stroke');
        if (s && s !== 'none' && s !== '#fff' && s !== '#ffffff' && s !== 'white') {
          node.setAttribute('stroke', ACCENT);
        }
      });

      document.querySelectorAll('.precircleBlock .conic, .conic').forEach((el) => {
        const angle = el.style.getPropertyValue('--angle') || '0%';
        const gradient = `conic-gradient(${ACCENT} ${angle}, ${TRACK} 0)`;
        el.style.setProperty('background-image', gradient, 'important');
        el.style.setProperty('background-color', 'transparent', 'important');
      });

      document.querySelectorAll('.ccName').forEach((el) => {
        el.style.setProperty('color', ACCENT, 'important');
      });
    };

    const applyVars = () => {
      const targets = [document.documentElement, document.body];
      const vars = {
        '--courses-accent-color': ACCENT,
        '--courses-primary-color': ACCENT,
        '--courses-text-color': '#ffffff',
        '--courses-desc-color': 'rgba(255, 255, 255, 0.72)',
        '--courses-title-bg-color': 'transparent',
        '--courses-block-border-color': 'rgba(255, 255, 255, 0.12)',
        '--courses-progress-bar-color': TRACK,
        '--courses-font-family': "var(--im-body, 'Manrope', sans-serif)",
      };
      targets.forEach((el) => {
        Object.entries(vars).forEach(([k, v]) => el.style.setProperty(k, v, 'important'));
      });
      retintProgress();
      polishCopy();
      ensureLessonTitle();
      polishShowMore();
      dedupeNavArrows();

      if (document.querySelector('.skeletonMainHeadBlock, .skeletonLeftSection, .skeletonContentBlock')) {
        setLoading(true);
      } else if (isCoursesReady()) {
        setLoading(false);
      }
    };

    const polishShowMore = () => {
      document.querySelectorAll('.showMoreTextC').forEach((el) => {
        const expanded = el.classList.contains('active');
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const i18n = window.IM_I18N || {};
        const showMore = i18n.showMore || 'Mostra di più';
        const showLess = i18n.showLess || 'Mostra meno';
        el.setAttribute('aria-label', expanded ? showLess : showMore);
        if (!el.dataset.imBound) {
          el.dataset.imBound = '1';
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              el.click();
            }
          });
        }
      });
    };

    /* CP arrowsposotion=bottom_plus_top injects #coursesNavArrows twice */
    const dedupeNavArrows = () => {
      const arrows = [...document.querySelectorAll('#coursesNavArrows')];
      if (arrows.length < 2) return;
      arrows.slice(0, -1).forEach((el) => el.remove());
    };

    ensureLoader();
    setLoading(true);
    applyVars();

    const bumpStylesheet = () => {
      const link = document.querySelector('link[href*="im-courses-plus"]');
      if (!link || !link.parentNode) return;
      link.parentNode.appendChild(link);
    };

    bumpStylesheet();
    window.setTimeout(bumpStylesheet, 400);
    window.setTimeout(applyVars, 400);
    window.setTimeout(applyVars, 1200);
    window.setTimeout(applyVars, 2500);
    window.setTimeout(() => setLoading(false), 8000);

    const host = document.querySelector('#MainContent') || document.body;
    const observer = new MutationObserver(() => {
      applyVars();
      if (!document.body.classList.contains('im-courses')) {
        document.body.classList.add('im-courses');
      }
    });
    observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  }

  onReady(() => {
    initStickyHeader();
    initAccountMenu();
    initReveals();
    /* Enable hide-until-reveal only after above-fold nodes already have --in (avoids NO_LCP). */
    document.documentElement.classList.add('im-motion-ready');
    initHeroParallax();
    initSectionParallax();
    initHeroVideo();
    initChapterRail();
    initMidTrailer();
    initTrailer();
    initStickyBuy();
    initSmoothAnchors();
    initCoursesPlusTheme();
  });
})();
